import { lazy, Suspense } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRootRouteWithContext, useRouter } from "@tanstack/react-router";
import { TooltipProvider } from "@sx/ui";

import { initializePostHog, readPostHogSettings } from "../posthog";
import { ThemeProvider, useConsoleTheme } from "../shell/theme";
import { NavigableOutlet } from "../shell/NotFound";

initializePostHog(readPostHogSettings(import.meta.env));

/* Toasts answer an action, so none can be asked for before the first paint; sonner (about
 * 9 KB gzipped) loads after it rather than inside the eager entry. */
const Toaster = lazy(() =>
  import("@sx/ui/components/ui/sonner").then((module) => ({ default: module.Toaster })),
);

/** Everything the router hands every route. One query client for the whole product — the
 * cache follows the user across workspaces, which is the point of merging the six SPAs. */
export interface ConsoleRouterContext {
  readonly queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<ConsoleRouterContext>()({
  component: RootLayout,
});

/* Providers only. Chrome (header, session gate, banners) belongs to the `_console` layout,
   because the kiosk display route deliberately renders none of it.

   One thing besides providers: `NavigableOutlet` below, the one gate that closes an
   address the quality ledger has not opened. Its placement is argued where it is defined
   (`shell/NotFound.tsx`); here it is enough that it must be above every route and below
   nothing, so that whatever is mounted outside the console layout cannot open on its
   own. */
function RootLayout() {
  // The router's own context, not this match's: a cold load draws `_console`'s pending
  // frame before the root match has settled a context of its own.
  const { queryClient } = useRouter().options.context;
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ThemedConsole />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function ThemedConsole() {
  const { dark } = useConsoleTheme();
  return (
    <TooltipProvider delayDuration={250}>
      <div className="h-full">
        <NavigableOutlet />
      </div>
      <Suspense fallback={null}>
        <Toaster
          theme={dark ? "dark" : "light"}
          position="bottom-right"
          richColors
        />
      </Suspense>
    </TooltipProvider>
  );
}
